/**
 * src/state/forecastControl.js
 *
 * Audit-Readiness Forecast — a deterministic, READ-ONLY forward-simulation of
 * the 6-gate compliance engine onto a user-chosen audit date T.
 *
 * This module NEVER writes state and never mutates a control, document,
 * evidence record, or vendor. It is pure projection over the state already in
 * memory (no Azure Function, no backend call).
 *
 * ── Correctness contract (the load-bearing property, AC-003) ──────────────────
 * The per-control projection reuses the EXACT gate logic in
 * src/state/evaluateControl.js. We do not re-implement any gate rule here.
 * `evaluateControl` exposes a single `asOf` seam (the evaluation instant); every
 * time-anchored comparison in the engine reads it. Therefore:
 *
 *     forecastControlAt(id, docs, ctx, asOf=now)
 *       === evaluateControl(id, docs, ctx)            // byte-for-byte
 *
 * This identity is asserted in forecastControl.test.js for every control.
 *
 * ── Degradation signals (four) ────────────────────────────────────────────────
 * Two flow through the control gates automatically via `asOf`:
 *   • Document review SLA  — Gate 2 (doc.nextReview past T → review overdue)
 *   • Obligation cadence   — Gate 4 (a satisfied risk obligation ages out of its
 *                            cadence+grace window before T → stops counting as
 *                            evidence)
 * Two are audit-prep renewals that do NOT map to a control gate in this codebase
 * but are surfaced in the "expiring before T" list so the user can schedule them:
 *   • Evidence expiration  — evidenceRecords[].validUntil
 *   • Training deadlines   — trainingModules[].assignments[].deadline
 *   • Vendor reassessment  — vendor.nextAssessmentDate
 *
 * Each list signal copies the date semantics from the corresponding server
 * sweeper so the forecast and reality agree (citations inline below).
 *
 * ── Held constant (cannot be predicted) ───────────────────────────────────────
 * Future task completion, new control assignments, future document publications,
 * Gate 5 obligation status, and Gate 6 ownership are held at today's value — we
 * have no intent data to project them. The existing useComplianceForecast hook
 * makes the same do-nothing-trajectory assumption; this module is its superset
 * (arbitrary T, gate-accurate per control, four signals vs. one).
 *
 * ISO 27001:2022 clause 9.1 — forward-looking monitoring & measurement evidence.
 */

import { evaluateControl } from './evaluateControl.js';
import { getAssessmentConfig, vendorRequiresAssessment } from '../utils/vendorQuestions.js';
import {
  trainingModulesOf,
  trainingProgramsOf,
  programsContainingModule,
} from '../utils/trainingDerivations.js';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Project a single control's gate status at instant `asOf`.
 *
 * Thin, intentional wrapper over evaluateControl: the forecast must reuse the
 * engine, never fork it. Keeping this as the named entry point makes the parity
 * test (forecastControlAt at asOf=now === evaluateControl) explicit and gives
 * callers a self-documenting name.
 *
 * @param {string} controlId
 * @param {Array}  documents      — same array evaluateControl reads
 * @param {object} domainContext  — same context object evaluateControl reads
 * @param {number|Date|string} asOf — evaluation instant; defaults to now
 * @returns {{ status, gates, docs }}
 */
export function forecastControlAt(controlId, documents, domainContext, asOf) {
  return evaluateControl(controlId, documents, domainContext, asOf);
}

/** Whole days from `fromMs` to `toMs`, floored, may be negative. */
function daysUntil(fromMs, toMs) {
  return Math.floor((toMs - fromMs) / DAY_MS);
}

/** Parse a date-ish value to ms, or NaN. */
function ms(v) {
  if (!v) return NaN;
  return new Date(v).getTime();
}

/**
 * Collect every state-resident item that will flip from "fine today" to "red"
 * strictly between `nowMs` and `targetMs` (inclusive of targetMs).
 *
 * Items already red today (date <= nowMs) are EXCLUDED — they are current
 * findings, not forecast events. Each item carries a `deepLink` to its source
 * record and `daysUntilRed` (whole days from now to the flip).
 *
 * @returns {{
 *   documents: Array, evidence: Array, training: Array, vendors: Array,
 *   total: number
 * }}
 */
export function collectExpiringItems(state, nowMs, targetMs) {
  const within = (whenMs) => Number.isFinite(whenMs) && whenMs > nowMs && whenMs <= targetMs;

  // ── 1. Document review SLA ────────────────────────────────────────────────
  // Mirrors azure-functions/shared/documents/reviewReminderSweeper.js, which
  // uses doc.nextReview as the operative due date, and evaluateControl Gate 2
  // (isOverdue(doc.nextReview)). A published doc whose nextReview falls before T
  // makes Gate 2 fail at T.
  const documents = [];
  for (const d of state.documents || []) {
    if (d.documentStatus !== 'published') continue;
    const whenMs = ms(d.nextReview);
    if (!within(whenMs)) continue;
    documents.push({
      kind: 'document',
      id: d.id,
      title: d.title || d.id,
      detail: 'Review due',
      date: d.nextReview,
      daysUntilRed: daysUntil(nowMs, whenMs),
      deepLink: `/documents?doc=${encodeURIComponent(d.id)}`,
    });
  }

  // ── 2. Evidence expiration ────────────────────────────────────────────────
  // Mirrors azure-functions/shared/evidence/scheduler.js EXPIRE pass: a record
  // with status='verified' flips to 'expired' when validUntil <= now.
  const evidence = [];
  for (const rec of state.evidenceRecords || []) {
    if (rec.status !== 'verified') continue;
    const whenMs = ms(rec.validUntil);
    if (!within(whenMs)) continue;
    evidence.push({
      kind: 'evidence',
      id: rec.id,
      title: rec.requirementName || rec.requirementId || 'Evidence record',
      detail: rec.employeeName ? `Expires — ${rec.employeeName}` : 'Expires',
      date: rec.validUntil,
      daysUntilRed: daysUntil(nowMs, whenMs),
      deepLink: '/employees',
    });
  }

  // ── 3. Training deadlines ─────────────────────────────────────────────────
  // Training assignments live on state.trainingModules[].assignments[] with a
  // `deadline` field (TrainingTab.jsx) AND on state.trainingPrograms[]
  // .assignments[], which is how the product's Programs section binds a module
  // to a population. A passed deadline is the "red" event.
  // Archived courses are excluded (no longer enforced), and so are archived
  // programmes — the employee portal already drops those, so an archived
  // programme's deadline is not owed by anyone.
  //
  // This walked `course.assignments` alone, so a programme-assigned deadline
  // produced no forecast event at all and a programme-only tenant's forecast
  // was silently cleaner than reality (GH #6457). The forecast's whole safety
  // argument is directional — it may project decay and may never project
  // improvement it cannot observe — so a class of decay it structurally cannot
  // see is that named property failing, not a missing feature.
  const training = [];
  for (const course of trainingModulesOf(state)) {
    if (course.archivedAt) continue;
    const sources = [
      { assignments: course.assignments, program: null },
      ...programsContainingModule(course, trainingProgramsOf(state)).map((p) => ({
        assignments: p.assignments,
        program: p,
      })),
    ];
    for (const { assignments, program } of sources) {
      for (const a of assignments || []) {
        const whenMs = ms(a.deadline);
        if (!within(whenMs)) continue;
        training.push({
          kind: 'training',
          id: program ? `${course.id}-${program.id}-${a.id}` : a.id,
          title: course.title || course.name || 'Training',
          detail: program
            ? `Completion deadline — ${program.name || program.title || 'programme'}`
            : 'Completion deadline',
          date: a.deadline,
          daysUntilRed: daysUntil(nowMs, whenMs),
          deepLink: '/employees',
        });
      }
    }
  }

  // ── 4. Vendor reassessment ────────────────────────────────────────────────
  // Mirrors azure-functions/shared/vendors/reassessmentSweeper.js: the cadence
  // is driven by vendor.nextAssessmentDate, and only actively-tracked vendors
  // that require an assessment are considered. We reuse the shared frontend
  // helpers (src/utils/vendorQuestions.js) so the "tracked vendor" predicate
  // matches the sweeper exactly.
  const vendors = [];
  const vendorConfig = getAssessmentConfig(state);
  for (const v of state.vendors || []) {
    if (!v || !v.id) continue;
    if (v.archivedAt) continue;
    if (v.status === 'inactive' || v.status === 'offboarded') continue;
    if (!vendorRequiresAssessment(vendorConfig, v)) continue;
    const whenMs = ms(v.nextAssessmentDate);
    if (!within(whenMs)) continue;
    vendors.push({
      kind: 'vendor',
      id: v.id,
      title: v.name || 'Vendor',
      detail: 'Reassessment due',
      date: v.nextAssessmentDate,
      daysUntilRed: daysUntil(nowMs, whenMs),
      deepLink: '/vendors',
    });
  }

  const sortByDays = (arr) => arr.sort((a, b) => a.daysUntilRed - b.daysUntilRed);
  sortByDays(documents);
  sortByDays(evidence);
  sortByDays(training);
  sortByDays(vendors);

  return {
    documents,
    evidence,
    training,
    vendors,
    total: documents.length + evidence.length + training.length + vendors.length,
  };
}

/**
 * Compute the projected gate-pass posture for a set of controls at instant
 * `targetMs`, against today (`nowMs`), using the shared `runForecast` closure
 * that callers wire to the live domainContext.
 *
 * `runForecast(controlId, asOf)` must delegate to forecastControlAt with the
 * tenant's documents + domainContext (see useAppState.forecastControlAt). This
 * keeps the forecast's "today" column identical to the rest of the app — both
 * read the same domainContext — and avoids re-deriving obligation statuses here.
 *
 * @param {string[]} controlIds
 * @param {(controlId: string, asOf?: number) => {status:string}} runForecast
 * @returns {{
 *   total, todayCompliant, projectedCompliant, todayPct, projectedPct,
 *   regressions: Array<{ controlId, todayStatus, projectedStatus }>
 * }}
 */
export function summariseForecast(controlIds, runForecast, nowMs, targetMs) {
  let todayCompliant = 0;
  let projectedCompliant = 0;
  const regressions = [];

  for (const controlId of controlIds) {
    const todayStatus = runForecast(controlId, nowMs).status;
    const projectedStatus = runForecast(controlId, targetMs).status;
    if (todayStatus === 'compliant') todayCompliant++;
    if (projectedStatus === 'compliant') projectedCompliant++;
    // A regression is any control whose status worsens by the target date.
    if (todayStatus === 'compliant' && projectedStatus !== 'compliant') {
      regressions.push({ controlId, todayStatus, projectedStatus });
    }
  }

  const total = controlIds.length;
  const pct = (n) => (total === 0 ? 0 : Math.round((n / total) * 100));

  return {
    total,
    todayCompliant,
    projectedCompliant,
    todayPct: pct(todayCompliant),
    projectedPct: pct(projectedCompliant),
    regressions,
  };
}
